import React, { useEffect, useState, useCallback, useRef } from "react";
import "./Orders.scss";
import {
  theme,
  Layout,
  Spin,
  notification,
  Input,
  Pagination,
  Typography,
  Button,
  Image,
  Segmented,
  Select,
  Badge,
} from "antd";
import {
  ArrowLeftOutlined,
  SearchOutlined,
  DownOutlined,
  MessageOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import useWindowWidth from "../../hooks/useWindowWidth";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { getOrders } from "../../features/orders/ordersActions";
import { resetOrdersError } from "../../features/orders/ordersSlice";
import { clearCard } from "../../features/card/cardSlice";
import {
  openChat,
  seedUnreadFromHistory,
} from "../../features/orderChat/orderChatSlice";
import { CustomEmpty, OrderChat } from "../../components";
import { ReorderModal } from "../../modals";
import product_logo from "../../assets/logo_product.svg";
import classNames from "classnames";
import Utils from "../../utils";

const { Content } = Layout;

const PER_PAGE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

// Status tabs, same five as the staff app's Orders page. Each one sends the
// exact status code; "Canceled" also folds in store-side cancellations so a
// customer can find every cancelled order under one tab. "" = All = no param.
const STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "In Progress", value: "IN_PROGRESS" },
  { label: "Finish preparing", value: "READY_FOR_ATTAINMENT" },
  { label: "Complete", value: "COMPLETE" },
  { label: "Canceled", value: "CANCELED" },
];

const getStatusParams = (value) => {
  if (value === "CANCELED") {
    return {
      status: undefined,
      statuses: ["CANCELED_CUSTOMER", "CANCELED_SELLER"],
    };
  }
  return {
    status: STATUS_OPTIONS?.some((el) => el?.value === value && el?.value)
      ? value
      : undefined,
    statuses: undefined,
  };
};

// Query for GET /orders/history/ built from the URL, so the mount fetch and a
// socket-triggered refetch ask for exactly the same page and filters. With
// `id` (a deep link from the notification bell) the server's exact filter is
// sent alone: one row or none, no paging, no tabs.
const getOrdersParams = (searchParams) => {
  const id = searchParams?.get("id");
  if (id) {
    return { id };
  }
  return {
    page: searchParams?.get("page") || 1,
    per_page: searchParams?.get("per_page") || PER_PAGE,
    name: searchParams?.get("search") || undefined,
    ...getStatusParams(searchParams?.get("status")),
  };
};

// Whether an order with `status` belongs under the active status tab. "" (All)
// and unknown tab values match everything, like the server does.
const statusMatchesFilter = (filterValue, status) => {
  const { status: single, statuses } = getStatusParams(filterValue);
  if (statuses) {
    return statuses?.includes(status);
  }
  if (single) {
    return single === status;
  }
  return true;
};

// Status pill palette (WeedVader style: tinted background, colored text and
// border). Anything still moving is gold, finished is green, cancelled or
// failed is red.
// Status pill colors come from the theme's Status group (the editor's
// "Status badges" section); background and border are lighter shades of the
// same color. The fallbacks are the theme.js defaults.
const statusPill = (color) => ({
  color,
  bg: `color-mix(in srgb, ${color} 15%, transparent)`,
  border: `color-mix(in srgb, ${color} 30%, transparent)`,
});

// Raw backend status code → pill label and colors. READY_FOR_ATTAINMENT is
// worded "Finish preparing" to match the staff app (CANASALE_FRONTEND).
const getStatusMeta = (status, token) => {
  const gold = statusPill(token?.Status?.warningColor || "#E8B339");
  const green = statusPill(token?.Status?.successColor || "#61BC18");
  const red = statusPill(token?.Status?.errorColor || "#ff4d4f");
  switch (status) {
    case "PENDING":
      return { label: "Pending", ...gold };
    case "IN_PROGRESS":
      return { label: "In progress", ...gold };
    case "READY_FOR_ATTAINMENT":
      return { label: "Finish preparing", ...gold };
    case "COMPLETE":
      return { label: "Complete", ...green };
    case "CANCELED_CUSTOMER":
      return { label: "Canceled", ...red };
    case "CANCELED_SELLER":
      return { label: "Canceled by store", ...red };
    case "FAILED":
      return { label: "Failed", ...red };
    default:
      return {
        label: status ? String(status)?.replace(/_/g, " ") : "",
        color: "color-mix(in srgb, var(--text-main, #fff) 85%, transparent)",
        bg: "color-mix(in srgb, var(--text-main, #fff) 8%, transparent)",
        border: "color-mix(in srgb, var(--text-main, #fff) 20%, transparent)",
      };
  }
};

// Only a finished order can be placed again; the backend refuses the rest
// with 400, so the button never shows for them.
const REORDERABLE_STATUSES = [
  "COMPLETE",
  "CANCELED_CUSTOMER",
  "CANCELED_SELLER",
];

const PAYMENT_LABELS = {
  cash: "Cash",
  cashless: "Cashless",
  apple_cash: "Apple Cash",
  apple_pay: "Apple Pay",
  credit_card: "Credit card",
  zelle: "Zelle",
  venmo: "Venmo",
  crypto: "Crypto",
};

const getPaymentLabel = (paymentType) => {
  if (!paymentType) return "—";
  return PAYMENT_LABELS[paymentType] || String(paymentType)?.replace(/_/g, " ");
};

const money = (value) => Utils?.roundNumber(Utils?.parsePrice(value));

const doSearch = (value, cb) => {
  cb(value);
};
const debouncedSearch = Utils?.debounce(doSearch, 400);

export default function Orders() {
  const { useToken } = theme;
  const { token } = useToken();
  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (type, title, text) => {
      api[type]({
        title: title,
        description: Utils.getErrorText(text),
        placement: "top",
      });
    },
    [api]
  );
  const { width, isMobile } = useWindowWidth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    loading,
    items: orders,
    meta,
    error,
    lastUpdate,
  } = useSelector((state) => state?.orders);
  // Chat is for logged-in customers only; guests never see the button.
  const isAuthenticated = useSelector((state) => state?.auth?.isAuthenticated);
  const unread = useSelector((state) => state?.orderChat?.unread);
  // Card whose chat drawer is open gets a green glow and a filled Chat button.
  const openChatOrderId = useSelector((state) => state?.orderChat?.openOrderId);
  const dispatch = useDispatch();
  const [searchVal, setSearchVal] = useState(searchParams?.get("search") || "");
  // Filters live in the URL like page/search, so a reload or a shared link
  // restores them and every change goes to the server.
  const statusFilter = searchParams?.get("status") || "";
  // `id` narrows the page to one order (a click in the notification bell);
  // `chat=1` also opens that order's chat once its row is here.
  const focusedOrderId = searchParams?.get("id") || "";
  const wantsChat = searchParams?.get("chat") === "1";
  // The order id the chat was already auto-opened for, so a row swapped in
  // place by the socket does not reopen a drawer the customer closed.
  const autoOpenedChatFor = useRef(null);
  // Ids of the orders whose item list is expanded.
  const [openIds, setOpenIds] = useState([]);
  // The history row the Quick Reorder modal is open for; null = closed.
  const [reorderOrder, setReorderOrder] = useState(null);
  // Sequence of the last socket push this page has handled; seeded with
  // whatever is in the store so a push from before the page mounted (the
  // mount fetch already reflects it) is not replayed as a refetch.
  const handledUpdateSeq = useRef(lastUpdate?.seq || 0);

  const setUrlSearchParams = (newParams) => {
    const filteredSearchParams = Utils?.get_filtered_url_params(searchParams, {
      page: searchParams?.get("page") ? searchParams?.get("page") : undefined,
      per_page: searchParams?.get("per_page")
        ? searchParams?.get("per_page")
        : undefined,
      search: searchParams?.get("search")
        ? searchParams?.get("search")
        : undefined,
      status: searchParams?.get("status")
        ? searchParams?.get("status")
        : undefined,
      ...newParams,
    });
    // Replace instead of push: the params only exist so a reload or a shared
    // link restores the list. Pushing them would stack one history entry per
    // keystroke/page, and the back arrow would crawl through them instead of
    // leaving the page.
    setSearchParams(filteredSearchParams, { replace: true });
  };

  useEffect(() => {
    dispatch(getOrders(getOrdersParams(searchParams)));
    setSearchVal(searchParams?.get("search") || "");
  }, [dispatch, searchParams]);

  // A fresh page of results opens the first order's items and collapses
  // the rest. Keyed on `meta`, which every fetch replaces, rather than on the
  // rows: a socket push that swaps one row in place leaves `meta` alone, so a
  // status change never folds the cards the customer is reading.
  useEffect(() => {
    setOpenIds(orders?.length ? [orders[0]?.id] : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta]);

  // Live status push from the shop socket (see useShopSocket). The reducer
  // has already replaced the row when it is on this page, and the pill
  // re-renders from it; no toast here, alerts are a separate notification
  // feature. The page only refetches when the list itself may be stale: the
  // order just appeared (`previous_status` null, so it is not on any page
  // yet) or a status tab is active and the order moved in or out of it. The
  // refetch keeps the current page, search and filter, so nothing is
  // appended locally.
  useEffect(() => {
    if (!lastUpdate?.seq || lastUpdate.seq === handledUpdateSeq.current) {
      return;
    }
    handledUpdateSeq.current = lastUpdate.seq;
    const { order, previous_status: previousStatus } = lastUpdate;
    if (!order?.id) {
      return;
    }
    const appeared = previousStatus === null || previousStatus === undefined;
    const filterChanged =
      Boolean(statusFilter) &&
      statusMatchesFilter(statusFilter, previousStatus) !==
        statusMatchesFilter(statusFilter, order?.status);
    if (appeared || filterChanged) {
      dispatch(getOrders(getOrdersParams(searchParams)));
    }
  }, [lastUpdate, statusFilter, searchParams, dispatch]);

  // Every page of history carries `unread_message_count` per order; it is the
  // server's truth for the badges and overwrites whatever the socket built up.
  useEffect(() => {
    if (orders?.length) {
      dispatch(seedUnreadFromHistory(orders));
    }
  }, [orders, dispatch]);

  // Deep link with `chat=1`: open the chat of the focused order as soon as
  // its row is in state, once per id. Opening the chat marks that order's
  // message lines read on the server, which drops the bell on its own.
  useEffect(() => {
    if (!focusedOrderId || !wantsChat || !isAuthenticated) {
      return;
    }
    if (autoOpenedChatFor.current === focusedOrderId) {
      return;
    }
    const row = orders?.find((o) => String(o?.id) === String(focusedOrderId));
    if (!row) {
      return;
    }
    autoOpenedChatFor.current = focusedOrderId;
    dispatch(
      openChat({
        orderId: row?.id,
        orderNumber: `${row?.order_id || row?.id}`,
      })
    );
  }, [focusedOrderId, wantsChat, isAuthenticated, orders, dispatch]);

  // "Show all orders": back to the plain list, page 1.
  const showAllOrders = () => {
    autoOpenedChatFor.current = null;
    setSearchParams({}, { replace: true });
  };

  const onSearchInpChange = (e) => {
    const value = e?.target?.value;
    setSearchVal(value);
    debouncedSearch(value, () => {
      setUrlSearchParams({
        page: 1,
        per_page: PER_PAGE,
        search: value ? value : undefined,
      });
    });
  };

  const onStatusChange = (val) => {
    // Filter changes always land back on page 1.
    setUrlSearchParams({
      page: 1,
      status: val ? val : undefined,
    });
  };

  const handlePagination = (e, a) => {
    Utils?.scrollIntoViewByClassName(".orders-cont");
    setUrlSearchParams({
      page: e,
      per_page: a,
    });
  };

  const toggleOpen = (id) => {
    setOpenIds((current) =>
      current?.includes(id)
        ? current?.filter((el) => el !== id)
        : [...current, id]
    );
  };

  useEffect(() => {
    if (location?.pathname === "/orders") {
      if (error && !loading) {
        for (let key in error) {
          openNotificationWithIcon("error", key, error[key]);
        }
        dispatch(resetOrdersError());
      }
    }
  }, [error, loading, location?.pathname, openNotificationWithIcon, dispatch]);

  const goToProduct = (item) => {
    // menu_item is null when the product was deleted later — the snapshot
    // still renders, it just isn't a link anymore.
    if (item?.menu_item) {
      navigate(`/product/${item?.menu_item}`);
      Utils?.scrollIntoViewByClassName(".appHeader-cont");
    }
  };

  const hasActiveNarrowing = Boolean(
    searchParams?.get("search") || statusFilter
  );

  const emptyDescription = focusedOrderId
    ? "This order is no longer available"
    : hasActiveNarrowing
      ? "Nothing matches your search"
      : "No orders yet";

  // The backend fills `address` for delivery and pickup orders alike.
  const getAddress = (order) => order?.address || "—";

  // The reorder POST also empties the customer's server basket, so the local
  // copy is cleared too; the list is refetched so the new order shows as the
  // first row of the current page and filter. No toast: the new row at the
  // top of the list is the confirmation.
  const onReorderPlaced = () => {
    setReorderOrder(null);
    dispatch(clearCard());
    dispatch(getOrders(getOrdersParams(searchParams)));
  };

  const renderStatusPill = (status) => (
    <span
      className="orders-cont__list__card__head__pill"
      style={{
        color: status?.color,
        backgroundColor: status?.bg,
        borderColor: status?.border,
      }}>
      {status?.label}
    </span>
  );

  const renderField = (label, value, extraClass) => (
    <div
      className={classNames(
        "orders-cont__list__card__head__field",
        extraClass
      )}>
      <Typography.Text className="orders-cont__list__card__head__field__label">
        {label}
      </Typography.Text>
      <Typography.Text
        className="orders-cont__list__card__head__field__value"
        style={{
          color: token?.Typography?.mainColorText,
        }}
        ellipsis={{
          tooltip: {
            title: value,
          },
        }}>
        {value}
      </Typography.Text>
    </div>
  );

  const renderToggle = (order, isOpen) => (
    <Button
      type="text"
      size="small"
      className="orders-cont__list__card__head__toggleBtn"
      aria-expanded={isOpen}
      aria-label={isOpen ? "Hide details" : "Show details"}
      onClick={() => {
        toggleOpen(order?.id);
      }}
      style={{
        color: token?.Typography?.colorPrimaryText,
      }}>
      Details
      <DownOutlined
        className={classNames(
          "orders-cont__list__card__head__toggleBtn__chevron",
          {
            "orders-cont__list__card__head__toggleBtn__chevron--open": isOpen,
          }
        )}
      />
    </Button>
  );

  // Chat button with the unread badge, icon-only (the aria-label carries the
  // name). `orderId` is the numeric row id the chat endpoints key on;
  // `orderNumber` is only for the label.
  const renderChatBtn = (order, orderNumber) => {
    if (!isAuthenticated) {
      return null;
    }
    const isChatOpen = openChatOrderId === order?.id;
    return (
      <Badge
        count={unread?.[order?.id] || 0}
        size="small"
        offset={[-4, 4]}
        className="orders-cont__list__card__head__chatBadge">
        <Button
          type={isChatOpen ? "primary" : "text"}
          size="small"
          className="orders-cont__list__card__head__chatBtn"
          aria-label={`Chat about order ${orderNumber}`}
          aria-pressed={isChatOpen}
          icon={<MessageOutlined />}
          onClick={() => {
            dispatch(openChat({ orderId: order?.id, orderNumber }));
          }}
          style={
            isChatOpen
              ? undefined
              : {
                  color: token?.Typography?.colorPrimaryText,
                }
          }
        />
      </Badge>
    );
  };

  // Reorder button, beside Chat, icon-only like it. Only for a logged-in
  // customer and only on a finished order (see REORDERABLE_STATUSES).
  const renderReorderBtn = (order, orderNumber) => {
    if (!isAuthenticated || !REORDERABLE_STATUSES?.includes(order?.status)) {
      return null;
    }
    return (
      <Button
        type="text"
        size="small"
        className="orders-cont__list__card__head__reorderBtn"
        aria-label={`Reorder order ${orderNumber}`}
        icon={<ReloadOutlined />}
        onClick={() => {
          setReorderOrder(order);
        }}
        style={{
          color: token?.Typography?.colorPrimaryText,
        }}
      />
    );
  };

  return (
    <>
      {contextHolder}
      {isAuthenticated ? <OrderChat /> : null}
      {isAuthenticated && reorderOrder ? (
        <ReorderModal
          order={reorderOrder}
          token={token}
          onCancel={() => {
            setReorderOrder(null);
          }}
          onPlaced={onReorderPlaced}
        />
      ) : null}
      <Content
        className="orders-cont"
        style={{
          backgroundColor: token?.Layout?.contentBg,
        }}>
        <div className="orders-cont__header">
          <Button
            className="orders-cont__header__backBtn"
            type="text"
            size="small"
            aria-label="Go back"
            icon={
              <ArrowLeftOutlined
                style={{
                  fontSize: "24px",
                  color: token?.icons?.whiteIcon,
                }}
              />
            }
            onClick={() => {
              navigate(-1);
            }}
          />
          <Typography.Text
            className="orders-cont__header__title"
            style={{
              color: token?.Typography?.mainColorText,
            }}>
            Orders
          </Typography.Text>
        </div>

        {focusedOrderId ? (
          // One order from a notification: the tabs and search make no
          // sense here, only the way back to the full list.
          <div className="orders-cont__focusRow">
            <Typography.Text
              className="orders-cont__focusRow__hint"
              style={{
                color: token?.Typography?.mainColorText,
              }}>
              Showing one order from your notifications
            </Typography.Text>
            <Button
              type="link"
              size="small"
              className="orders-cont__focusRow__showAllBtn"
              onClick={showAllOrders}
              style={{
                color: token?.Typography?.colorPrimaryText,
              }}>
              Show all orders
            </Button>
          </div>
        ) : (
          <div className="orders-cont__filterRow">
            <Input
              className="orders-cont__filterRow__searchInp"
              size="large"
              placeholder="Search by order number, address or product"
              prefix={<SearchOutlined />}
              allowClear
              onChange={onSearchInpChange}
              value={searchVal}
            />
            {!isMobile ? (
              <Segmented
                className="orders-cont__filterRow__segmented"
                // Same height as the large search input beside it.
                size="large"
                disabled={loading}
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={onStatusChange}
              />
            ) : (
              // From 900px down the segments become a select with the same
              // options.
              <Select
                className="orders-cont__filterRow__select"
                size="large"
                disabled={loading}
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={onStatusChange}
              />
            )}
          </div>
        )}

        {!focusedOrderId ? (
          <div className="orders-cont__countWrapper">
            <Typography.Text
              className="orders-cont__countWrapper__number"
              style={{
                color: token?.Typography?.colorPrimaryText,
              }}>
              {meta?.count ? meta?.count : 0}
            </Typography.Text>
            <Typography.Text className="orders-cont__countWrapper__label">
              Orders
            </Typography.Text>
          </div>
        ) : null}

        <Spin
          size="large"
          spinning={loading}
          classNames={{
            root: classNames({
              "orders-cont__list__spinWrapper": loading || !orders?.length,
            }),
          }}>
          <div className="orders-cont__list">
            {!orders?.length && !loading && focusedOrderId ? (
              <div className="orders-cont__list__focusEmpty">
                <CustomEmpty
                  circleColor={
                    token?.icons?.emptyCircleIconColor
                      ? token?.icons?.emptyCircleIconColor
                      : ""
                  }
                  boxBorderColor={
                    token?.icons?.emptyIconBoxBorderColor
                      ? token?.icons?.emptyIconBoxBorderColor
                      : ""
                  }
                  description={emptyDescription}
                />
                <Button
                  type="link"
                  onClick={showAllOrders}
                  style={{
                    color: token?.Typography?.colorPrimaryText,
                  }}>
                  Show all orders
                </Button>
              </div>
            ) : !orders?.length && !loading ? (
              <CustomEmpty
                circleColor={
                  token?.icons?.emptyCircleIconColor
                    ? token?.icons?.emptyCircleIconColor
                    : ""
                }
                boxBorderColor={
                  token?.icons?.emptyIconBoxBorderColor
                    ? token?.icons?.emptyIconBoxBorderColor
                    : ""
                }
                description={emptyDescription}
              />
            ) : (
              orders?.map((order) => {
                const status = getStatusMeta(order?.status, token);
                const orderNumber = `${order?.order_id || order?.id}`;
                const dateStr = order?.date_ordered
                  ? dayjs(order?.date_ordered)?.format("MMM D, YYYY")
                  : "—";
                const items = order?.items || [];
                const isOpen = openIds?.includes(order?.id);
                const isChatOpen = openChatOrderId === order?.id;
                return (
                  <div
                    key={order?.id}
                    className={classNames("orders-cont__list__card", {
                      "orders-cont__list__card--open": isOpen,
                      "orders-cont__list__card--chatOpen": isChatOpen,
                    })}
                    // Green outline marks the order whose chat is open.
                    style={
                      isChatOpen
                        ? { borderColor: token?.colorPrimary }
                        : undefined
                    }>
                    <div
                      className="orders-cont__list__card__head"
                      style={{
                        backgroundColor: token?.Card?.colorBgCard,
                      }}>
                      {!isMobile ? (
                        <>
                          {renderField("Order #", orderNumber)}
                          {renderField("Ordered at", dateStr)}
                          {renderField(
                            "Address",
                            getAddress(order),
                            "orders-cont__list__card__head__field--address"
                          )}
                          {renderField(
                            "Payment type",
                            getPaymentLabel(order?.payment_type)
                          )}
                          {renderField("Total", money(order?.grand_total))}
                          <div className="orders-cont__list__card__head__field">
                            <Typography.Text className="orders-cont__list__card__head__field__label">
                              Status
                            </Typography.Text>
                            {renderStatusPill(status)}
                          </div>
                          <div className="orders-cont__list__card__head__actions">
                            {renderReorderBtn(order, orderNumber)}
                            {renderChatBtn(order, orderNumber)}
                            {renderToggle(order, isOpen)}
                          </div>
                        </>
                      ) : (
                        // Phones: the strip folds into rows, like the
                        // WeedVader mobile header.
                        <div className="orders-cont__list__card__head__mobile">
                          <div className="orders-cont__list__card__head__mobile__row">
                            <Typography.Text
                              className="orders-cont__list__card__head__field__value"
                              style={{
                                color: token?.Typography?.mainColorText,
                              }}>
                              #{orderNumber}
                            </Typography.Text>
                            <Typography.Text
                              className="orders-cont__list__card__head__field__value"
                              style={{
                                color: token?.Typography?.mainColorText,
                              }}>
                              {dateStr}
                            </Typography.Text>
                          </div>
                          <div className="orders-cont__list__card__head__mobile__row">
                            <Typography.Text className="orders-cont__list__card__head__field__label">
                              Address
                            </Typography.Text>
                            <Typography.Text
                              className="orders-cont__list__card__head__field__value orders-cont__list__card__head__mobile__row__grow"
                              style={{
                                color: token?.Typography?.mainColorText,
                              }}
                              ellipsis={{
                                tooltip: {
                                  title: getAddress(order),
                                },
                              }}>
                              {getAddress(order)}
                            </Typography.Text>
                          </div>
                          <div className="orders-cont__list__card__head__mobile__row">
                            <div className="orders-cont__list__card__head__mobile__row__pair">
                              <Typography.Text className="orders-cont__list__card__head__field__label">
                                Payment type
                              </Typography.Text>
                              <Typography.Text
                                className="orders-cont__list__card__head__field__value"
                                style={{
                                  color: token?.Typography?.mainColorText,
                                }}>
                                {getPaymentLabel(order?.payment_type)}
                              </Typography.Text>
                            </div>
                            <div className="orders-cont__list__card__head__mobile__row__pair">
                              <Typography.Text className="orders-cont__list__card__head__field__label">
                                Total
                              </Typography.Text>
                              <Typography.Text
                                className="orders-cont__list__card__head__field__value"
                                style={{
                                  color: token?.Typography?.mainColorText,
                                }}>
                                {money(order?.grand_total)}
                              </Typography.Text>
                            </div>
                          </div>
                          <div className="orders-cont__list__card__head__mobile__row">
                            {renderStatusPill(status)}
                            <div className="orders-cont__list__card__head__actions">
                              {renderReorderBtn(order, orderNumber)}
                              {renderChatBtn(order, orderNumber)}
                              {renderToggle(order, isOpen)}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {isOpen
                      ? items?.map((item, index) => {
                          const isLink = Boolean(item?.menu_item);
                          // `sale_price` is what the customer paid per unit;
                          // orders from before it existed carry it equal to
                          // `price`, so they render as "no sale".
                          const paid = item?.sale_price ?? item?.price;
                          const onSale =
                            item?.sale_price !== null &&
                            item?.sale_price !== undefined &&
                            Utils?.parsePrice(item?.sale_price) !==
                              Utils?.parsePrice(item?.price);
                          return (
                            <div
                              key={`${order?.id}-line-${item?.variant ?? index}`}
                              className="orders-cont__list__card__item"
                              style={{
                                backgroundColor: token?.colorFillTertiary,
                              }}>
                              <div className="orders-cont__list__card__item__imageWrapper">
                                <Image
                                  alt={item?.name || "product"}
                                  src={
                                    item?.picture_url
                                      ? item?.picture_url
                                      : product_logo
                                  }
                                  preview={true}
                                  width="100%"
                                  height="100%"
                                  style={{
                                    objectFit: "contain",
                                  }}
                                />
                              </div>
                              <div className="orders-cont__list__card__item__body">
                                {/* Only the name is the link, so the green hover
                                shows when the pointer is on the name itself. */}
                                <Typography.Text
                                  className={classNames(
                                    "orders-cont__list__card__item__body__name",
                                    {
                                      "orders-cont__list__card__item__body__name--link":
                                        isLink,
                                    }
                                  )}
                                  style={{
                                    color: token?.Typography?.mainColorText,
                                  }}
                                  ellipsis={{
                                    tooltip: {
                                      title: item?.name || "",
                                    },
                                  }}
                                  onClick={() => {
                                    goToProduct(item);
                                  }}>
                                  {item?.name || ""}
                                </Typography.Text>
                                <div className="orders-cont__list__card__item__body__priceRow">
                                  {onSale ? (
                                    // List price struck through in green,
                                    // same as the checkout table.
                                    <Typography.Text
                                      delete
                                      className="orders-cont__list__card__item__body__priceRow__oldPrice"
                                      style={{
                                        color:
                                          token?.Typography?.colorPrimaryText,
                                      }}>
                                      {money(item?.price)}
                                    </Typography.Text>
                                  ) : null}
                                  <Typography.Text
                                    className="orders-cont__list__card__item__body__priceRow__price"
                                    style={{
                                      color: token?.Typography?.mainColorText,
                                    }}>
                                    {money(paid)}
                                  </Typography.Text>
                                  <Typography.Text className="orders-cont__list__card__item__body__priceRow__muted">
                                    {item?.quantity ?? 0}x
                                  </Typography.Text>
                                  {item?.variant_name ? (
                                    <Typography.Text className="orders-cont__list__card__item__body__priceRow__muted">
                                      {item?.variant_name}
                                    </Typography.Text>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      : null}
                  </div>
                );
              })
            )}
          </div>
        </Spin>
        {focusedOrderId ? null : (
          <Pagination
            className="orders-cont__pagination"
            disabled={loading}
            pageSize={Number(searchParams?.get("per_page")) || PER_PAGE}
            current={Number(searchParams?.get("page")) || 1}
            defaultCurrent={Number(searchParams?.get("page")) || 1}
            total={meta?.count ? meta?.count : ""}
            responsive={true}
            onChange={handlePagination}
            showQuickJumper={width < 584 && width >= 577 ? false : true}
            // Customers may want a long list at once; the choice lives in the
            // URL (per_page) like the page number.
            showSizeChanger
            pageSizeOptions={PAGE_SIZE_OPTIONS}
          />
        )}
      </Content>
    </>
  );
}
